import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';

import { setIsTipOnlyEnabled } from '@reducers/storeChecks';
import { setErrorPopUp } from '@reducers/notification';
import { checkOpen } from '@reducers/checks';
import { orderFinalize, driveSplitCheck } from '@reducers/payment';
import { setSeat, setPaymentCheck } from '@reducers/orders';
import { changeJob, logout, setLoggingOut } from '@reducers/oauth';
import { APP_CONFIG } from '@constants';
import EventEmitter from '@utility/eventSubscribers';
import { disconnect } from '../../../../sockets/communicator';

import { employeeCashOut } from '@reducers/cashOut';
import { getFormatedPhoneNumber } from '@utility/helperFunction';
import { getObjectValue } from '@utility/utills';
import { CUSTOM_MESSAGES } from '@veriables';

export const INITIAL_INPUTS = {
  checkName: '',
  orderId: '',
  amount1: '',
  amount2: '',
  totalAmount: '0.00',
};

export const useHamburgerActionHandler = ({
  allChecksVisible,
  getAllChecks,
  storeId,
  removeSeat,
  setVisible,
  showOpenChecks,
  handleToggleHamburgerMenu,
  thirdPartyAppName,
  onOpenTab,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const auth = useSelector(state => state.oauth.authInfo);
  const deviceInfo = useSelector(state => state.oauth.deviceInformation);
  const userInfo = useSelector(state => state.oauth.loggedInUserData);
  const nativeAppVersion = useSelector(state => state.oauth.nativeAppVersion);
  const environment = useSelector(state => state.oauth.environment);

  const [isOfflineScreen, setIsOfflineScreen] = useState(false);
  const [isAboutShow, setAboutTipShow] = useState(false);
  const [aboutData, setAboutData] = useState(null);
  const [inputs, setInputs] = useState(INITIAL_INPUTS);

  const [isChangeJob, setIsChangeJob] = useState(false);
  const [jobErrors, setJobErrors] = useState('');
  const [jobDefaultValue, setJobDefaultValue] = useState(userInfo?.jobId);

  const employeeJobHashTable = useSelector(state => state.oauth.employeeJobHashTable);
  const optionForDropdown = useMemo(() => Object.values(employeeJobHashTable || {}).filter(
    e => e.allowEmployeeJobAssignment
  ), [employeeJobHashTable]);

  const handleChangeJobClick = useCallback(() => {
    setIsChangeJob(prev => !prev);
    setJobDefaultValue(userInfo?.jobId);
  }, [userInfo]);

  const submitChangeJob = useCallback((jobId) => {
    if (!jobId) return;
    dispatch(setLoggingOut(true));
    dispatch(changeJob({
      jobId: jobId || null,
      employeeId: userInfo?.id,
      storeId: userInfo?.storeId,
    }))
      .then(() => {
        setIsChangeJob(false);
        const dataToSend = {
          latitude: '92',
          longitude: '92',
          deviceId: deviceInfo?.deviceId,
          employeeId: userInfo?.externalId,
          employeeCard: userInfo?.employeeCard,
        };
        dispatch(logout(dataToSend));
        navigate('/checkIn', { replace: true });
      })
      .catch(e => {
        setIsChangeJob(false);
        dispatch(setLoggingOut(false));
        dispatch(setErrorPopUp({
          res: e.message,
        }));
      });
  }, [dispatch, userInfo, deviceInfo, navigate]);

  const onJobDropdownChange = useCallback((id) => {
    submitChangeJob(id);
  }, [submitChangeJob]);


  const handleLoyaltyAccountBalance = useCallback(() => {
    navigate(`${APP_CONFIG.BASE_URL}/loyalty-balance`, { replace: true });
    handleToggleHamburgerMenu();
  }, [navigate, handleToggleHamburgerMenu]);

  const handleGiftCardBalance = useCallback(() => {
    window.sessionStorage.setItem('giftCard_type', 'balance');
    navigate(`${APP_CONFIG.BASE_URL}/giftcard/${123}?type=balance`, {
      replace: true,
    });
    handleToggleHamburgerMenu();
  }, [navigate, handleToggleHamburgerMenu]);

  const handleGameCardBalance = useCallback(() => {
    navigate(`${APP_CONFIG.BASE_URL}/gamecard/${123}?type=balance`, {
      replace: true,
    });
    handleToggleHamburgerMenu();
  }, [navigate, handleToggleHamburgerMenu]);

  const lauchExternalApp = useCallback(() => {
    const url = auth?.thirdPartyAppUrl;
    if (!url) return;
    const constructUrl = url
      .replace('{brandId}', auth.brandId)
      .replace('{storeId}', deviceInfo?.storeId)
      .replace('{storeExternalId}', auth.id)
      .replace('{deviceId}', deviceInfo?.deviceId)
      .replace('{storeNumber}', auth.storeNumber)
      .replace('{employeeId}', deviceInfo?.employeeId || userInfo?.id)
      .replace('{checkId}', 0)
      .replace('{checkNumber}', 0)
      .replace('{tableNumber}', 0);

    if (window.ReactNativeWebView) {
      EventEmitter.lauchExternalApp({
        url: constructUrl,
      });
    } else {
      window.location = constructUrl;
    }
    handleToggleHamburgerMenu();
    disconnect();
    window.sessionStorage.clear();
  }, [auth, deviceInfo, userInfo, handleToggleHamburgerMenu]);

  const handleOfflineScreen = useCallback(() => {
    setIsOfflineScreen(true);
    dispatch(setIsTipOnlyEnabled(false));
  }, [dispatch]);

  const handleTipOnly = useCallback(() => {
    dispatch(setIsTipOnlyEnabled(true));
    onOpenTab();
    handleToggleHamburgerMenu();
  }, [dispatch, onOpenTab, handleToggleHamburgerMenu]);

  const handleChecks = useCallback(() => {
    if (allChecksVisible) {
      getAllChecks(storeId, true).then(() => {
        removeSeat();
        setVisible();
        handleToggleHamburgerMenu();
      });
    } else {
      showOpenChecks().then(() => {
        removeSeat();
        setVisible();
        handleToggleHamburgerMenu();
      });
    }
  }, [allChecksVisible, getAllChecks, storeId, removeSeat, setVisible, showOpenChecks, handleToggleHamburgerMenu]);

  const splitFinalise = useCallback(
    ({ data = {} }) => {
      const dataToSendToAdd = {
        deviceId: deviceInfo?.deviceId,
        employeeId: userInfo?.id,
        checkHeaderId: data?.id,
        storeId: data?.storeId,
        unpaidAmount: data?.unpaidAmount,
        totalAmount: data?.totalAmount,
        paidAmount: data?.paidAmount,
        splitType: 0,
      };
      dispatch(driveSplitCheck(dataToSendToAdd))
        .then(res => {
          const syncedCheck = res?.check || data;
          let updatedData = {
            ...syncedCheck,
            control: res?.control,
            checkHeaderId: res?.checkHeaderId,
            storeId: res?.storeId,
            giftCard: res?.giftCard,
            offer: res?.offer,
            paymentCheck: res?.paymentCheck,
            paymentChecks: res?.paymentChecks,
            availableLoyaltyOffers: res?.availableLoyaltyOffers,
            check: res?.check,
          };
          dispatch(setSeat(updatedData));
          dispatch(setPaymentCheck(updatedData));
          navigate(
            `${APP_CONFIG.BASE_URL}/receipt/${res?.paymentCheck?.id}?ticketSplitTypeId=1&splitValue=1&screenType=4&type=payType`,
            { replace: true }
          );
        })
        .catch(err => {
          dispatch(setErrorPopUp({
            res: err.message,
          }));
        });
    },
    [deviceInfo, userInfo, dispatch, navigate]
  );

  const handleOfflinePayment = useCallback(() => {
    const dataToSend = {
      deviceId: deviceInfo?.deviceId,
      employeeId: Number(userInfo?.id),
      storeId: deviceInfo?.storeId,
      stationId: null,
      employeeCard: userInfo?.employeeCard,
      stationGroupId: null,
      isTab: true,
      phoneNumber: null,
      pickupTime: null,
      guestName: null,
      guestCount: 1,
      sourceType: 14,
      delivery: false,
      speedOrder: false,
      name: inputs?.checkName,
      number: inputs?.orderId,
      subtotalAmount: +inputs?.amount1,
      taxAmount: +inputs?.amount2,
      totalAmount: +inputs?.totalAmount
    };
    dispatch(checkOpen(dataToSend))
      .then((res) => {
        const dataToSend = {
          deviceId: deviceInfo?.deviceId,
          employeeId: userInfo?.id,
          storeId: deviceInfo?.storeId,
          items: res?.items,
          checkHeaderId: res?.id,
          name: res?.name,
          number: res?.number,
          subtotalAmount: res?.subtotalAmount || 0,
          taxAmount: res?.taxAmount || 0,
          totalAmount: res?.totalAmount || 0
        };
        dispatch(orderFinalize(dataToSend))
          .then(resData => {
            splitFinalise({ data: resData });
          })
          .catch(err => {
            dispatch(setErrorPopUp({
              res: err.message,
            }));
          });
      })
      .catch(err => {
        dispatch(setErrorPopUp({
          res: err.message,
        }));
      });
  }, [deviceInfo, userInfo, inputs, dispatch, splitFinalise]);

  const handleCashOut = useCallback(() => {
    const dataToSend = {
      storeId: userInfo?.storeId,
      employeeId: userInfo?.id,
      deviceId: deviceInfo?.deviceId,
    };
    const eventObj = {
      drawerIpAddress: deviceInfo?.drawerIpAddress,
      drawerPort: deviceInfo?.drawerPort,
    };

    const handleDrawerStatusCheck = () => {
      let retryCount = 0;
      const checkStatus = () => {
        retryCount += 1;
        EventEmitter.drawerStatus(eventObj, res => {
          setTimeout(() => {
            if ([1].includes(res.status) && retryCount <= 3) {
              return checkStatus();
            }
            if (res.status === 0) {
              navigate('/cashOut/bills', {
                replace: true,
                state: { drawerOpen: true },
              });
              const intervalId = setInterval(() => {
                EventEmitter.drawerStatus(eventObj, resStatus => {
                  if ([9, 1].includes(resStatus.status)) {
                    clearInterval(intervalId);
                  }
                });
              }, 1000);
              return;
            }
            dispatch(setErrorPopUp({
              res: `${CUSTOM_MESSAGES().DRAWER_CASHOUT_FAIL}`,
            }));
          }, 1000);
        });
      };
      checkStatus();
    };

    if (userInfo?.supportsCashDrawer && deviceInfo?.storeDrawerId) {
      dispatch(employeeCashOut({ ...dataToSend }))
        .then(() => {
          if (window.ReactNativeWebView) {
            EventEmitter.drawerOpen(eventObj);
            handleDrawerStatusCheck();
          }
        })
        .catch(err => {
          dispatch(setErrorPopUp({ res: err.message }));
        });
    } else {
      dispatch(employeeCashOut(dataToSend))
        .then(res => {
          if (res?.jobType !== 4) {
            navigate('/report/server/cashOut', {
              state: { response: res },
              replace: true,
            });
          } else if (res?.jobType === 4 && !userInfo?.supportsCashDrawer) {
            dispatch(setErrorPopUp({
              res: CUSTOM_MESSAGES().EMPLOYEE_DRAWER_FAIL,
            }));
          } else {
            if (window.ReactNativeWebView) {
              EventEmitter.drawerOpen(eventObj);
              handleDrawerStatusCheck();
            }
          }
        })
        .catch(err => {
          dispatch(setErrorPopUp({
            res: err.message,
            noErrorTxt: true,
          }));
        });
    }
    handleToggleHamburgerMenu();
  }, [userInfo, deviceInfo, dispatch, navigate, handleToggleHamburgerMenu]);

  const handleAbout = useCallback(() => {
    const dataTosend = {
      store: auth?.name,
      device: deviceInfo?.deviceName,
      appVersion: nativeAppVersion,
      webVersion: APP_CONFIG.VERSION,
      environment: environment,
      forSupport: getObjectValue(
        () => getFormatedPhoneNumber(auth?.supportPhoneNumber),
        ''
      ),
      deviceGroup: deviceInfo?.deviceGroup
        ? deviceInfo?.deviceGroup?.name
        : '',
    };
    setAboutData({ isOpen: true, val: dataTosend });
    setAboutTipShow(true);
  }, [auth, deviceInfo, nativeAppVersion, environment]);

  const clickHanlder = useMemo(() => ({
    allChecks: handleChecks,
    openChecks: handleChecks,
    loyaltyAccountBalance: handleLoyaltyAccountBalance,
    giftBalance: handleGiftCardBalance,
    gameCardBalance: handleGameCardBalance,
    lauchExternalApp: lauchExternalApp,
    offlinePayment: handleOfflineScreen,
    tipOnly: handleTipOnly,
    cashOut: handleCashOut,
    changeJobType: handleChangeJobClick,
    about: handleAbout,
  }), [
    handleChecks,
    handleLoyaltyAccountBalance,
    handleGiftCardBalance,
    handleGameCardBalance,
    lauchExternalApp,
    handleOfflineScreen,
    handleTipOnly,
    handleCashOut,
    handleChangeJobClick,
    handleAbout,
  ]);

  return {
    isOfflineScreen,
    setIsOfflineScreen,
    isAboutShow,
    setAboutTipShow,
    aboutData,
    inputs,
    setInputs,
    clickHanlder,
    handleOfflinePayment,
    isChangeJob,
    setIsChangeJob,
    jobErrors,
    setJobErrors,
    jobDefaultValue,
    setJobDefaultValue,
    optionForDropdown,
    onJobDropdownChange,
  };
};
